import { backToLogin } from '@/helpers'
import {
  setToken,
  setRefreshToken,
  setUser as setStoredUser,
  removeToken,
  removeRefreshToken,
  removeUser,
  removeUserEmail,
  removeRoleId,
  removeSidebarDetails,
  getToken,
  getUser,
} from '@/lib/storage'
import { authService } from '@/services/api/auth'
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Role } from '@/types/roles'

// ---- Role mapping from role_id ----
const ROLE_MAP: Record<number, string> = {
  1: Role.ADMIN,
  2: Role.CALLING_AGENT,
  3: Role.SALES_AGENT,
}

// ---- Normalize raw API user to app User shape ----
function normalizeApiUser(raw: any): User {
  return {
    id: String(raw.id ?? raw.uuid ?? ''),
    uuid: raw.uuid ?? '',
    email: raw.email ?? '',
    username: raw.username ?? '',
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
    role: raw.role ?? ROLE_MAP[raw.role_id] ?? Role.CALLING_AGENT,
    roleId: raw.role_id ?? null,
    status: raw.status ?? 'active',
    mobile: raw.mobile ?? '',
    address: raw.address ?? '',
    city: raw.city ?? '',
    state: raw.state ?? '',
    country: raw.country ?? '',
    pincode: raw.pincode ?? '',
    gender: raw.gender ?? '',
    dob: raw.dob ?? '',
    profile: raw.profile ?? '',
  }
}

export interface User {
  id: string
  uuid: string
  email: string
  username: string
  firstName: string
  lastName: string
  role: string
  roleId: number | null
  status: string
  [key: string]: any
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface ProfileUpdateData {
  first_name?: string
  last_name?: string
  gender?: string
  dob?: string
  address?: string
  city?: string
  state?: string
  country?: string
  pincode?: string
  mobile?: string
  profile?: string
}

export interface ChangePasswordData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const token = getToken()
const savedUser = getUser()

const initialState: AuthState = {
  user: savedUser ? normalizeApiUser(savedUser) : null,
  isAuthenticated: !!token && !!savedUser,
  isLoading: false,
  error: null,
}

// ---- Thunks ----

export const login = createAsyncThunk<any, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await authService.login(credentials)
      const user = normalizeApiUser(res?.attributes?.user ?? {})
      const accessToken: string = res?.attributes?.tokens?.accessToken ?? ''
      const refreshToken: string = res?.attributes?.tokens?.refreshToken ?? ''
      setToken(accessToken)
      setRefreshToken(refreshToken)
      setStoredUser(res?.attributes?.user ?? {})

      return { user, message: res?.message }
    } catch (err: any) {
      const msg = err?.message || 'Login failed'
      return rejectWithValue(msg)
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const { getRefreshToken } = await import('@/lib/storage')
      const refreshToken = getRefreshToken() ?? ''

      removeToken()
      removeRefreshToken()
      removeUser()
      removeUserEmail()
      removeRoleId()
      removeSidebarDetails()

      await authService.logout(refreshToken).catch(() => {})
      backToLogin()
      return null
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed'
      return rejectWithValue(errorMessage)
    }
  }
)

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const res = await authService.forgotPassword({ email })
      return res
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to send reset email')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: ProfileUpdateData, { rejectWithValue }) => {
    try {
      const res = await authService.updateProfile(data)
      return res
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Profile update failed')
    }
  }
)

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: ChangePasswordData, { rejectWithValue }) => {
    try {
      const res = await authService.changePassword(data)
      return res
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to change password')
    }
  }
)

// ---- Slice ----

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.isAuthenticated = true
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.error = action.payload as string
      })

    // Forgot password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Update profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false
        if (state.user && action.payload?.attributes?.user) {
          state.user = normalizeApiUser(action.payload.attributes.user)
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Change password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setUser } = authSlice.actions
export default authSlice.reducer